import React, { useState } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

export function ProfilePage() {
  const { t } = useLang()
  const { user, accessToken, logoutOthers, refreshUser } = useAuth()
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [username, setUsername] = useState(user?.username || '')
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [logoutMsg, setLogoutMsg] = useState('')

  if (!user) return null

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)
    const body: Record<string, string> = {}
    if (displayName !== user.display_name) body.display_name = displayName
    if (username !== (user.username || '')) body.username = username
    if (newPassword) {
      body.old_password = oldPassword
      body.new_password = newPassword
    }
    try {
      const r = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      })
      if (r.ok) {
        setSuccess(t('profileSaved'))
        setOldPassword('')
        setNewPassword('')
        await refreshUser()
      } else {
        const err = await r.json()
        setError(err?.error?.message || t('errorGeneric'))
      }
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setSaving(false)
    }
  }

  const handleLogoutOthers = async () => {
    try {
      await logoutOthers()
      setLogoutMsg(t('logoutOthersDone'))
    } catch {
      setLogoutMsg(t('errorGeneric'))
    }
  }

  const readonlyStyle: React.CSSProperties = {
    padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '4px',
    fontSize: '14px', background: 'var(--bg-page)', color: 'var(--text-muted)',
  }
  const inputStyle: React.CSSProperties = {
    padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px',
  }
  const labelStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--text-secondary)' }

  return (
    <div style={{ maxWidth: '480px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', marginBottom: '24px' }}>{t('myAccount')}</h2>

      {success && (
        <div style={{ padding: '10px 12px', marginBottom: '16px', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: '4px', color: 'var(--success)', fontSize: '13px' }}>
          {success}
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 12px', marginBottom: '16px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <fieldset style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '16px' }}>
          <legend style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 6px' }}>{t('basicInfo')}</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('email')}</label>
              <input value={user.email} readOnly style={readonlyStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('role')}</label>
              <input value={user.role} readOnly style={readonlyStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('region')}</label>
              <input value={user.region_code} readOnly style={readonlyStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('group')}</label>
              <input value={user.group_name} readOnly style={readonlyStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('displayName')} (1–32)</label>
              <input
                type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                maxLength={32} required style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('username')} (3–32, 字母数字下划线)</label>
              <input
                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                minLength={3} maxLength={32} style={inputStyle}
              />
            </div>
          </div>
        </fieldset>

        <fieldset style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '16px' }}>
          <legend style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 6px' }}>{t('changePassword')}</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('oldPassword')}</label>
              <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={labelStyle}>{t('newPassword')}</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </fieldset>

        <button type="submit" disabled={saving} style={{
          padding: '10px', background: saving ? 'var(--text-muted)' : 'var(--accent)',
          color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px', fontSize: '14px',
          fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
        }}>
          {saving ? t('loading') : t('saveChanges')}
        </button>
      </form>

      <div style={{ marginTop: '32px', borderTop: '1px solid var(--border)', paddingTop: '24px' }}>
        <h3 style={{ fontSize: '15px', marginBottom: '12px' }}>{t('sessions')}</h3>
        <button onClick={handleLogoutOthers} style={{
          padding: '8px 16px', background: 'var(--bg-surface)', color: 'var(--danger)',
          border: '1px solid var(--danger)', borderRadius: '4px', fontSize: '14px', cursor: 'pointer',
        }}>
          {t('logoutOthers')}
        </button>
        {logoutMsg && <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>{logoutMsg}</p>}
      </div>
    </div>
  )
}
